// InternProgressPanel — company-facing view of a hired intern's task & hours
// tracking. Fetched from GET /stn/interns/applications/:id/progress, which
// aggregates the intern's Jira board(s). Lets a StartupNexus founder see at a
// glance how their intern is doing: assigned vs logged vs completed hours,
// per-epic rollups, and a full task breakdown — without any manual chasing.

import React, { useState, useEffect } from 'react';
import {
  Clock, Timer, CheckCircle2, Hourglass, Gauge, ListChecks, Layers,
  Loader2, RefreshCw, AlertTriangle, Inbox,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell,
} from 'recharts';
import { getInternProgress } from '@/api/stnApi';

const STATUS_META = {
  todo:        { label: 'To Do',       color: '#94a3b8' },
  in_progress: { label: 'In Progress', color: '#3b82f6' },
  in_review:   { label: 'In Review',   color: '#06b6d4' },
  testing:     { label: 'Testing',     color: '#8b5cf6' },
  done:        { label: 'Done',        color: '#22c55e' },
  blocked:     { label: 'Blocked',     color: '#ef4444' },
};
const TYPE_COLOR = { epic: '#f97316', story: '#a855f7', task: '#3b82f6', subtask: '#06b6d4', bug: '#ef4444' };

export default function InternProgressPanel({ appId }) {
  const [loading, setLoading] = useState(true);
  const [data, setData]       = useState(null);
  const [err, setErr]         = useState(null);

  const load = () => {
    setLoading(true); setErr(null);
    getInternProgress(appId)
      .then((res) => setData(res?.data ?? res))
      .catch((e) => setErr(e?.message || 'Could not load progress'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (appId) load(); /* eslint-disable-next-line */ }, [appId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-10 text-muted-foreground text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading task &amp; hours tracking…
      </div>
    );
  }
  if (err) {
    return (
      <div className="flex flex-col items-center gap-2 py-8 text-muted-foreground text-sm">
        <AlertTriangle className="w-5 h-5 text-amber-500" /> {err}
        <button onClick={load} className="text-primary hover:underline text-xs mt-1">Try again</button>
      </div>
    );
  }

  const s = data?.summary || {};
  const tasks = data?.tasks || [];
  const byEpic = data?.byEpic || [];
  const statusCounts = data?.statusCounts || {};

  if (!tasks.length) {
    return (
      <div className="flex flex-col items-center gap-2 py-10 text-muted-foreground text-sm">
        <Inbox className="w-6 h-6" />
        <p className="font-medium text-foreground/70">No tracked tasks yet</p>
        <p className="text-xs text-center max-w-xs">
          Once this intern's mentor assigns tasks with time estimates, their hours and progress will appear here.
        </p>
        <button onClick={load} className="inline-flex items-center gap-1 text-primary hover:underline text-xs mt-1">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>
    );
  }

  const statusBars = Object.entries(statusCounts).map(([k, v]) => ({
    name: STATUS_META[k]?.label || k, count: v, color: STATUS_META[k]?.color || '#94a3b8',
  }));

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          {data?.batches?.length ? `Batches: ${data.batches.join(', ')}` : 'Aggregated across assigned batches'}
        </p>
        <button onClick={load} className="inline-flex items-center gap-1 text-primary hover:underline text-xs">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
        <Kpi icon={Clock}        label="Assigned"   value={`${s.assignedHours ?? 0}h`}  sub={`${s.totalTasks ?? 0} tasks`} tone="indigo" />
        <Kpi icon={Timer}        label="Logged"     value={`${s.loggedHours ?? 0}h`}    sub={`${s.utilizationPct ?? 0}% util`} tone="blue" />
        <Kpi icon={CheckCircle2} label="Completed"  value={`${s.completedHours ?? 0}h`} sub={`${s.doneTasks ?? 0} done`} tone="green" />
        <Kpi icon={Hourglass}    label="Remaining"  value={`${s.remainingHours ?? 0}h`} sub={`${s.taskCompletionPct ?? 0}% tasks`} tone="amber" />
      </div>

      {/* Progress bars */}
      <div className="grid sm:grid-cols-2 gap-3">
        <Gauge_ title="Time Utilization" icon={Gauge} pct={s.utilizationPct ?? 0}
          caption={`${s.loggedHours ?? 0}h of ${s.assignedHours ?? 0}h`} color={(s.utilizationPct ?? 0) > 100 ? '#ef4444' : '#3b82f6'} />
        <Gauge_ title="Task Completion" icon={ListChecks} pct={s.taskCompletionPct ?? 0}
          caption={`${s.doneTasks ?? 0} of ${s.totalTasks ?? 0} tasks`} color="#22c55e" />
      </div>

      {/* Status distribution */}
      {statusBars.length > 0 && (
        <Panel title="Tasks by Status">
          <ResponsiveContainer width="100%" height={170}>
            <BarChart data={statusBars} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => [`${v} tasks`, '']} />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {statusBars.map((b, i) => <Cell key={i} fill={b.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      )}

      {/* By epic */}
      {byEpic.length > 0 && (
        <Panel title="Hours by Epic" icon={Layers}>
          <div className="space-y-2.5">
            {byEpic.map((e) => {
              const pct = e.assignedHours ? Math.min(100, Math.round((e.loggedHours / e.assignedHours) * 100)) : 0;
              return (
                <div key={e.name}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-medium truncate">{e.name}</span>
                    <span className="text-muted-foreground tabular-nums">{e.loggedHours}h / {e.assignedHours}h · {e.done}/{e.total}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: pct > 100 ? '#ef4444' : '#6366f1' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Panel>
      )}

      {/* Task table */}
      <Panel title={`Task Breakdown (${tasks.length})`}>
        <div className="overflow-x-auto -mx-1">
          <table className="w-full text-xs min-w-[520px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="px-2 py-1.5">Task</th>
                <th className="px-2 py-1.5">Epic</th>
                <th className="px-2 py-1.5 text-right">Assigned</th>
                <th className="px-2 py-1.5 text-right">Logged</th>
                <th className="px-2 py-1.5 w-28">Progress</th>
                <th className="px-2 py-1.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((t, i) => {
                const sm = STATUS_META[t.status] || STATUS_META.todo;
                const over = t.loggedHours > t.assignedHours && t.assignedHours > 0;
                return (
                  <tr key={i} className="border-b border-border/40 hover:bg-muted/20">
                    <td className="px-2 py-1.5 max-w-[180px]">
                      <span className="font-mono text-[9px] text-muted-foreground mr-1">{t.key}</span>
                      <span className="font-medium" style={{ color: TYPE_COLOR[t.type] || undefined }}>{t.title}</span>
                    </td>
                    <td className="px-2 py-1.5 text-muted-foreground truncate max-w-[100px]">{t.epic || '—'}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{t.assignedHours}h</td>
                    <td className={`px-2 py-1.5 text-right tabular-nums font-medium ${over ? 'text-red-600' : ''}`}>{t.loggedHours}h</td>
                    <td className="px-2 py-1.5">
                      <div className="flex items-center gap-1">
                        <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${Math.min(100, t.progressPct)}%`, background: over ? '#ef4444' : t.status === 'done' ? '#22c55e' : '#3b82f6' }} />
                        </div>
                        <span className="text-[9px] text-muted-foreground w-7 text-right">{t.progressPct}%</span>
                      </div>
                    </td>
                    <td className="px-2 py-1.5">
                      <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full" style={{ color: sm.color, background: sm.color + '1a' }}>{sm.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}

const TONE = {
  indigo: 'text-indigo-600 bg-indigo-50 border-indigo-200',
  blue:   'text-blue-600 bg-blue-50 border-blue-200',
  green:  'text-green-600 bg-green-50 border-green-200',
  amber:  'text-amber-600 bg-amber-50 border-amber-200',
};

const Kpi = ({ icon: Icon, label, value, sub, tone }) => (
  <div className="rounded-xl border border-border p-3">
    <div className={`inline-flex h-6 w-6 rounded-lg items-center justify-center border mb-1.5 ${TONE[tone] || TONE.blue}`}>
      <Icon className="w-3.5 h-3.5" />
    </div>
    <p className="text-lg font-bold leading-none">{value}</p>
    <p className="text-[10px] text-muted-foreground mt-1">{label} · {sub}</p>
  </div>
);

const Gauge_ = ({ title, icon: Icon, pct, caption, color }) => (
  <div className="rounded-xl border border-border p-3">
    <div className="flex items-center justify-between mb-1.5">
      <span className="text-xs font-semibold flex items-center gap-1.5"><Icon className="w-3.5 h-3.5 text-primary" /> {title}</span>
      <span className="text-base font-bold tabular-nums" style={{ color }}>{pct}%</span>
    </div>
    <div className="h-2 rounded-full bg-muted overflow-hidden">
      <div className="h-full rounded-full" style={{ width: `${Math.min(100, pct)}%`, background: color }} />
    </div>
    <p className="text-[10px] text-muted-foreground mt-1">{caption}</p>
  </div>
);

const Panel = ({ title, icon: Icon, children }) => (
  <div className="rounded-xl border border-border p-3">
    <h4 className="text-xs font-semibold flex items-center gap-1.5 mb-2.5">
      {Icon && <Icon className="w-3.5 h-3.5 text-primary" />} {title}
    </h4>
    {children}
  </div>
);
